import { bboxPolygon } from '@turf/turf';
import { useCallback, useEffect, useState } from 'react';

import getPlaces from '@/endpoints/mapbox/getPlaces';
import { IPlaceFeature } from '@/models/search';

import {
  SearchResult,
  SearchResultGeometry,
  SearchResultType,
} from '../models';

export const usePlaceSearch = (location: string) => {
  const [places, setPlaces] = useState<SearchResult<IPlaceFeature>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setPlaces([]);
  }, [location]);

  const fetchPlaces = useCallback(
    async (signal: AbortSignal) => {
      if (!location) return;

      setLoading(true);
      await getPlaces(location, signal)
        .then(({ res, err }) => {
          if (err) return;
          if (res) {
            setPlaces(
              res.data.features
                .slice(0, 3)
                .map((x) => {
                  let geometry: SearchResultGeometry | null = null;

                  // Build out a SearchResult for the mapbox results
                  // Favors bbox, then geometry, then center

                  if (x.center) {
                    geometry = {
                      coordinates: x.center,
                      type: 'Point',
                    };
                  }
                  if (x.geometry) {
                    geometry = x.geometry as SearchResultGeometry;
                  }
                  if (x.bbox) {
                    geometry = bboxPolygon(x.bbox).geometry;
                  }

                  if (!geometry) return null;
                  return {
                    geometry,
                    id: x.id,
                    label: x.place_name,
                    original: x,
                    type: SearchResultType.MAPBOX_PLACE,
                  };
                })
                .filter((x) => x !== null) as SearchResult<IPlaceFeature>[]
            );
          }
        })
        .finally(() => setLoading(false));
    },
    [location]
  );

  useEffect(() => {
    const abortController = new AbortController();
    fetchPlaces(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchPlaces]);

  return {
    loading,
    places,
  };
};
